// FerrousMetalsPage.js
import React from 'react';
import { Box, Typography, Container, Grid, List, ListItem, ListItemText, Breadcrumbs, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
// Removed import of PageLayout as it is no longer used

const FerrousMetalsPage = () => {
  // Define breadcrumbs directly within the component
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Metals & Materials', path: '/metals-materials' },
    { name: 'Ferrous Metals', path: '/metals-materials/ferrous' },
  ];

  const ferrousMetalTypes = [
    {
      name: 'Steel (Heavy Insize)',
      description: 'High-density steel scrap, typically from demolition or industrial sources, that fits within specific size constraints for efficient furnace charging. It\'s a primary component in electric arc furnaces (EAFs).',
      applications: ['Construction', 'Heavy Machinery', 'Automotive parts'],
      purity: 'Generally high purity, but contamination with non-ferrous metals or excessive rust can reduce value.',
      relevance: 'Crucial for steel production, a cornerstone of modern infrastructure.'
    },
    {
      name: 'Steel (Oversize)',
      description: 'Steel scrap that exceeds the standard dimensions for direct furnace charging. Requires pre-processing (shearing or torching) before it can be recycled.',
      applications: ['Large structural components', 'Industrial equipment', 'Shipbreaking'],
      purity: 'Similar to heavy insize, but requires more processing due to size.',
      relevance: 'Important source of steel, but incurs higher handling and processing costs.'
    },
    {
    name: 'Steel (Cars / Shredded)',
      description: 'Scrap derived from end-of-life vehicles (ELVs) that has been shredded into smaller, manageable pieces. It\'s a mix of ferrous metals, with some non-ferrous and non-metallic contaminants.',
      applications: ['New steel production', 'Foundries'],
      purity: 'Lower purity than heavy insize due to mixed composition; requires further separation (e.g., magnetic separation, eddy current separation).',
      relevance: 'Major volume of scrap, vital for automotive recycling and circular economy goals.'
    },
    {
      name: 'Cast Iron',
      description: 'A ferrous alloy with a high carbon content, making it brittle but excellent for casting. Common sources include old machinery, pipes, and engine blocks.',
      applications: ['Engine blocks', 'Pipes', 'Fittings', 'Cookware'],
      purity: 'Relatively high purity for casting applications; needs to be free from non-metallic attachments.',
      relevance: 'Valuable for specific casting applications where its properties are desired.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Ferrous Metals
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Ferrous metals, primarily iron and its alloys, are the backbone of industries worldwide. They are magnetic and prone to rust, but highly recyclable. Explore the common types and their characteristics below.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {ferrousMetalTypes.map((metal, index) => (
            <Box
              key={index}
              sx={{
                mb: 6, // Margin between sections
                p: { xs: 3, md: 5 }, // Padding for each section
                borderRadius: 4,
                background: 'background.paper', // Clean white background
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)', // Modern, subtle shadow
                border: '1px solid',
                borderColor: 'grey.200', // Light grey border for definition
                transition: 'all 0.3s ease-in-out', // Smooth transition for hover
                '&:hover': {
                  transform: 'translateY(-5px)', // Lift effect on hover
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)', // Enhanced shadow on hover
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {metal.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {metal.description}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Typical Applications:
                    </Typography>
                    <List dense disablePadding>
                      {metal.applications.map((app, appIndex) => (
                        <ListItem key={appIndex} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText primary={app} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Purity Considerations:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                      {metal.purity}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Market Relevance:
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {metal.relevance}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FerrousMetalsPage;
